import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import 'global-nav/dist/navbar.js';
import { MatSidenav } from '@angular/material/sidenav';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import {
  CookieService,
  I18nService,
  LoaderService,
  NavbarService,
  ProjectService,
  ScenarioService,
  SidenavService,
  TreeService,
} from '@service';
import { environment } from 'src/environments/environment.prd';

@Component({
  selector: 'optima-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('searchInput') input: ElementRef;
  sidenavOpen = true;
  disabledInput = true;
  mode: 'over' | 'push' | 'side' = 'side';

  images = [
    '../assets/svg-icons/arrow.svg',
    '../assets/svg-icons/general-info.svg',
    '../assets/images/arrow-forward.png',
    '../assets/svg-icons/info-circle.svg',
    '../assets/svg-icons/info.svg',
    '../assets/svg-icons/grid-white.svg',
    '../assets/svg-icons/delete-red.svg',
    '../assets/svg-icons/search.svg',
    '../assets/svg-icons/search-not.svg',
    '../assets/svg-icons/filter.svg',
    '../assets/svg-icons/map-pin.svg',
    '../assets/svg-icons/manage-white.svg',
    '../assets/svg-icons/edit-white.svg',
    '../assets/svg-icons/delete.svg',
    '../assets/svg-icons/spin.svg',
    '../assets/svg-icons/edit.svg',
  ];
  loaded = 0;

  constructor(
    private translateService: TranslateService,
    private i18nService: I18nService,
    private cookieService: CookieService,
    private sidenavService: SidenavService,
    private projectService: ProjectService,
    private treeService: TreeService,
    private scenarioService: ScenarioService,
    public loaderService: LoaderService,
    protected navbarService: NavbarService,
  ) {
    // this.loaderService.setExcludedUiUrls('tab=results');
    this.loaderService.regexEndpoints = [
      'ess-parameters',
      'permissions',
      'run',
      'site-parameters',
      'grid-services-parameters',
      'programs',
    ];
  }

  ngOnInit(): void {
    const { defaults } = environment;
    const locale = this.cookieService.getI18NLocale() || defaults.locale;
    const brand =
      this.cookieService.getI18NBrand() || defaults.brand.toUpperCase();
    this.translateService.setDefaultLang(
      `${defaults.locale}.${defaults.brand.toUpperCase()}`,
    );

    this.i18nService.setBrand(brand);
    this.i18nService.setLocale(locale);
    this.i18nService.loadLocale();

    this.translateService.use(`${locale}.${brand}`).subscribe(() => {
      document.title = this.translateService.instant('APP.TITLE');
    });

    this.sidenavService.close$.subscribe(() => (this.sidenavOpen = false));

    this.projectService.getProjects$().subscribe();
    this.scenarioService.getAllScenarios$().subscribe();

    this.treeService.enableFilterInput$.subscribe(
      enable => (this.disabledInput = !enable),
    );
  }

  async ngAfterViewInit(): Promise<void> {
    // TODO: Fix this
    // Awaits for the user object in global nav to have the user timezone
    // while (!Object.keys(this?.globalNav?.user).length) {
    //   await timeout(100);
    // }
    // this.timezoneService.userTimezone =
    //   this.globalNav.user.preferences?.default_time_zone;
    // this.userService.id = this.globalNav.user.user_id;
    //
    // const logo = document.querySelector('.logo') as HTMLElement;
    // logo.style.cursor = 'pointer';
    // logo.addEventListener('click', () => {
    //   this.router.navigateByUrl('/');
    // });

    this.sidenavService.setSidenav(this.sidenav);

    fromEvent(this.input.nativeElement, 'keyup')
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        tap(async () => {
          this.projectService.sendValue(this.input.nativeElement.value);
        }),
      )
      .subscribe();
  }

  isLargeScreen(): boolean {
    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;
    return width > 1280;
  }

  sidenavContentStyle(): CSSStyleDeclaration {
    const style = window.getComputedStyle(document.body);
    const dockWidth = +style
      .getPropertyValue('--sidenav-dock-width')
      .match(/\d+/)[0];
    const fullWidthWidth = document.querySelector('mat-sidenav').clientWidth;
    const matContentMargin = this.sidenavOpen
      ? `${fullWidthWidth - 30}px`
      : `${dockWidth + 5}px`;
    return {
      marginLeft:
        this.mode !== 'over'
          ? matContentMargin
          : `${dockWidth + 5}px !important`,
    } as CSSStyleDeclaration;
  }

  getMode(): 'over' | 'push' | 'side' {
    this.mode = this.isLargeScreen() ? 'side' : 'over';
    return this.mode;
  }

  toggleSidenav(): void {
    this.sidenavOpen = !this.sidenavOpen;
    this.sidenavService.sendToggleEvent();
  }

  mainContainerStyle(): CSSStyleDeclaration {
    if (!this.sidenavOpen) {
      return {} as CSSStyleDeclaration;
    }
    const style = window.getComputedStyle(document.body);
    const dockWidth = +style
      .getPropertyValue('--sidenav-dock-width')
      .match(/\d+/)[0];
    return {
      paddingLeft:
        this.mode !== 'over' ? '25px' : `${dockWidth + 25}px !important`,
    } as CSSStyleDeclaration;
  }

  load(): void {
    this.loaded++;
    if (this.images.length == this.loaded) {
      const imagesToLoad = Array.from(
        document.querySelectorAll('.image-to-load'),
      );
      imagesToLoad.forEach(image => {
        image.remove();
      });
    }
  }
}
